import { useEffect, useEffectEvent, useImperativeHandle, useReducer, useRef, useState, type Ref } from "react";
import { ApiError, newIdempotencyKey } from "../../api/client";
import { prepare, resend } from "../../api/pending";
import type {
  Authorization,
  Call,
  Channel,
  CustomerDetail,
  Discount,
  LineRequest,
  Order,
  ParkedTicket,
  Product,
  Quote,
} from "../../api/types";
import { testCallOffered } from "../../app/demo";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatPhone, phoneDigits } from "../../lib/phone";
import { Dialog, dialogOpen } from "../../ui/Dialog";
import { AllergenCard } from "../allergens/AllergenCard";
import { openFolder } from "../folder/open-folder";
import { CallsDialog } from "../calls/CallsDialog";
import { checkoutDiscounts, discountLabel } from "../general/general";
import { ReceiptDialog } from "../orders/ReceiptDialog";
import { editToasts, printToasts, toKitchen, unroutedToast } from "../printing/print-notices";
import { MoveTableDialog } from "../tables/MoveTableDialog";
import { TablePicker } from "../tables/TablePicker";
import {
  cartFromOrder,
  cartFromTicket,
  cartReducer,
  differsFrom,
  discountChanged,
  editChanges,
  emptyCart,
  missingDetails,
  noCustomer,
  toContent,
  toCreateRequest,
  toEditRequest,
  toParkRequest,
  type Cart,
  type CartLine,
} from "./cart";
import { CheckoutDialog, type Loyalty } from "./CheckoutDialog";
import { CodeBox } from "./CodeBox";
import { repeatLines } from "./customer";
import type { CustomerFieldsHandle } from "./CustomerFields";
import { DiscountButtons } from "./DiscountButtons";
import { lineChanges } from "./edit-changes";
import { EditSettleDialog, type Settle } from "./EditSettleDialog";
import { MenuPanel } from "./MenuPanel";
import { ProductDialog } from "./ProductDialog";
import { defaultVariant, findByCode, hasOptions, lineFor, normalizedLine, oneTap } from "./quick-entry";
import { pendingFreeOffer, Ticket } from "./Ticket";
import { useQuote } from "./useQuote";

/** What the sale screen starts with: an order reopened for editing, or a parked ticket. */
export type SaleStart =
  | { kind: "edit"; order: Order; authorization: Authorization | null }
  | { kind: "resume"; id: string; ticket: ParkedTicket };

/** What the rest of the till may ask the sale screen (the Wachtlijst before "Verder werken"). */
export interface SaleHandle {
  /** Something is on the ticket, or an order is being edited. */
  busy: () => boolean;
  editing: () => boolean;
  /** Parkeren: the ticket goes to the Wachtlijst; false when nothing was parked. */
  park: () => Promise<boolean>;
}

type Open =
  | { kind: "product"; product: Product; editing?: CartLine; variant?: string; quantity?: number; promotion?: true }
  | { kind: "checkout" }
  /** Paying the open order of a table (after saving what was added on the ticket). */
  | { kind: "payTable"; order: Order }
  | { kind: "tables" }
  | { kind: "moveTable"; from: string }
  | { kind: "payDifference"; order: Order }
  | { kind: "giveBack"; order: Order; amountCents: number }
  /** "Korting" while an order is edited (Bewerken): the shop's discounts and "Geen". */
  | { kind: "editDiscount" }
  /** "Bijbetalen" or "Terug te geven" before an edit of a paid order is saved. */
  | { kind: "settle"; totalCents: number; changes: { added: string[]; removed: string[] } }
  /** "Kassabon" right after a delivery is ordered (prototype). */
  | { kind: "receipt"; order: Order }
  /** De Bellerslijst (F10). */
  | { kind: "calls" }
  | { kind: "allergens" }
  | null;

/** "☎ Test oproep" (prototype CALLS_DEMO), only in the demo: Yasmine, Jens and an unknown number, in turn. */
const TEST_CALLS = ["0472184490", "0495663012", "0489123456"];

function startCart(start: SaleStart | null): Cart {
  if (start?.kind === "edit") return cartFromOrder(start.order);
  if (start?.kind === "resume") return cartFromTicket(start.ticket);
  return emptyCart();
}

interface Props {
  ref?: Ref<SaleHandle>;
  start: SaleStart | null;
  /** The sale screen is the view on top: only then its keys work (it stays loaded behind the others). */
  active: boolean;
  /** The edit is saved (with the updated order) or stopped (null). */
  onEditDone: (order: Order | null) => void;
}

/** The main screen: the ticket on the left, the menu on the right. */
export function SaleScreen({ ref, start, active, onEditDone }: Props) {
  const till = useTill();
  const {
    api,
    menu: menuResponse,
    promotions,
    toast,
    report,
    authorize,
    openTables,
    orderSaved,
    soldOut,
    callHandled,
    paymentMethods,
    general,
  } = till;
  const reopened = start?.kind === "edit" ? start : null;
  const editing = reopened?.order ?? null;
  const [cart, dispatch] = useReducer(cartReducer, start, startCart);
  /**
   * A phone: the menu or the order, one at a time under two tabs (Furkan, 18/10/2026); an order opened to edit
   * shows the order. A till pc shows both side by side and has no tabs.
   */
  const [pane, setPane] = useState<"menu" | "order">(start?.kind === "edit" ? "order" : "menu");
  /**
   * Whoever entered their code for something free or a discount on this order: asked once per
   * order, until "Nieuw" (as in the prototype). Their name goes into the history.
   */
  const [discountBy, setDiscountBy] = useState<Authorization | null>(null);
  /** The code that goes with saving: the discount's, else the one that allowed editing a paid order. */
  const staffToken = discountBy?.token ?? reopened?.authorization?.token;
  const [open, setOpen] = useState<Open>(null);
  const [saving, setSaving] = useState(false);
  const [category, setCategory] = useState<string | null>(null);
  const keyCounter = useRef(0);
  const newKey = () => `line-${String(++keyCounter.current)}`;
  const customerFields = useRef<CustomerFieldsHandle>(null);
  const codeBox = useRef<HTMLInputElement>(null);
  const testCalls = useRef(0);

  /** The customer at checkout (takeaway, delivery): for the loyalty card (Spaarkaart). */
  const [found, setFound] = useState<{ phone: string; customer: CustomerDetail | null } | null>(null);
  const phone = cart.customer.phone.replace(/\D/g, "");
  const lookUp = open?.kind === "checkout" && cart.channel !== "table" && phone.length >= 9;
  useEffect(() => {
    if (!lookUp) return;
    let current = true;
    api
      .request<{ customer: CustomerDetail | null }>("GET", `/device/customers/lookup?phone=${phone}`)
      .then((answer) => {
        if (current) setFound({ phone, customer: answer.customer });
      })
      .catch(() => {
        // no card shown: the sale goes on
        if (current) setFound({ phone, customer: null });
      });
    return () => {
      current = false;
    };
  }, [api, lookUp, phone]);
  const customer = lookUp && found?.phone === phone ? found.customer : null;

  const content = toContent(cart);
  // a new menu or other actions price the same ticket again
  const quote = useQuote(
    api,
    content,
    `${String(menuResponse.version)}:${JSON.stringify(promotions)}:${JSON.stringify(till.delivery)}`,
  );
  const missing = missingDetails(cart);
  /** The free product of the action still to be chosen: no paying or ordering before (Furkan, 20/10/2026). */
  const freeToChoose = pendingFreeOffer(
    quote,
    cart.lines.some((line) => line.promotion === true),
    soldOut,
  );
  const delivery = cart.channel === "delivery";
  const selected = cart.lines.find((line) => line.key === cart.selected) ?? null;

  const menu = menuResponse.menu;
  const rules = menuResponse.rules;

  /** A new, empty ticket: the discount code is asked again for the next order. */
  const reset = () => {
    dispatch({ type: "clear" });
    setDiscountBy(null);
  };

  /** "Nieuw" (F1): a new ticket; while editing it stops the edit, nothing is changed (prototype). */
  const newOrder = () => {
    if (editing) onEditDone(null);
    else reset();
  };

  const stopEdit = () => {
    toast(t.editing.stopped);
    onEditDone(null);
  };

  const addLine = (line: LineRequest) => {
    dispatch({ type: "add", key: newKey(), line: menu ? normalizedLine(menu, line) : line });
  };

  /** Gratis: a code the first time in this order; taking it back needs none. */
  const toggleFree = async (line: CartLine) => {
    if (line.promotion === true) return; // the free product of the action stays free
    if (line.free !== true && discountBy === null) {
      const authorization = await authorize("discount", t.codes.free);
      if (!authorization) return;
      setDiscountBy(authorization);
    }
    dispatch({ type: "toggleFree", key: line.key });
  };

  /** Returns false when the code window was closed: nothing was chosen. */
  const chooseDiscount = async (discount: Discount | null): Promise<boolean> => {
    if (discount !== null && discountBy === null) {
      const authorization = await authorize("discount", t.codes.discount);
      if (!authorization) return false;
      setDiscountBy(authorization);
    }
    dispatch({ type: "discount", discount });
    return true;
  };

  /**
   * "Korting" while editing (Furkan, 14/10/2026: a customer shows a voucher after paying): the discount goes
   * on the ticket, with the code as on a new order; "Opslaan" then shows what goes back to the customer
   * ("Terug te geven") or is still to pay, before anything is saved.
   */
  const discounts = checkoutDiscounts(general);
  const discountWhileEditing = editing !== null && (discounts.length > 0 || cart.discount !== null);
  const chooseEditDiscount = async (discount: Discount | null) => {
    if (await chooseDiscount(discount)) setOpen(null);
  };

  /**
   * "Korting" at the checkout of a table (Furkan, 13/10/2026: "Tafel afrekenen: geen −4 € korting"): the
   * table is already saved, so the server prices its bill again with the discount. A code as for a new
   * order, once per ticket; taking the discount away needs none.
   */
  const discountTable = async (order: Order, discount: Discount | null) => {
    let token = discountBy?.token;
    if (discount !== null && token === undefined) {
      const authorization = await authorize("discount", t.codes.discount);
      if (!authorization) return;
      setDiscountBy(authorization);
      token = authorization.token;
    }
    try {
      const discounted = await api.request<Order>("PUT", `/orders/${order.id}/discount`, {
        staffToken: token,
        idempotencyKey: newIdempotencyKey(),
        body: { expectedRevision: order.revision, discount },
      });
      setOpen({ kind: "payTable", order: discounted });
    } catch (error) {
      toast(error instanceof ApiError ? error.message : t.errors.unexpected);
    }
  };

  /**
   * Something free or a discount on a new order needs a code, once per order. After "Verder werken"
   * (Wachtlijst) the code is asked again before saving. Returns the token, undefined when none is
   * needed and null when the code window was closed.
   */
  const discountToken = async (): Promise<string | undefined | null> => {
    if (staffToken !== undefined) return staffToken;
    const needed = cart.discount !== null || cart.lines.some((line) => line.free === true && line.promotion !== true);
    if (!needed) return undefined;
    const authorization = await authorize("discount", t.codes.free);
    if (!authorization) return null;
    setDiscountBy(authorization);
    return authorization.token;
  };

  /**
   * A tile (prototype renderGrid): a tile with a size, a drink or a product with one price and nothing
   * to choose goes on the ticket at once; anything else opens the dialog at its default size.
   */
  const tapProduct = (product: Product, variant?: string) => {
    if (!menu) return;
    const size = variant ?? (oneTap(menu, product) ? defaultVariant(product) : undefined);
    if ((size !== undefined || product.variants.length <= 1) && !hasOptions(menu, product)) {
      addLine(lineFor(product, { variant: size, quantity: 1 }));
      return;
    }
    const preset = size ?? defaultVariant(product);
    setOpen({ kind: "product", product, ...(preset !== undefined && { variant: preset }) });
  };

  /** Whether the shop makes its folder now (its own print, ADR 0052): asked when ⚠ Allergenen opens. */
  const [folderAvailable, setFolderAvailable] = useState(false);
  const allergensOpen = open?.kind === "allergens";
  useEffect(() => {
    if (!allergensOpen) return;
    let stopped = false;
    api
      .request<{ available: boolean }>("GET", "/device/folder/available")
      .then((answer) => {
        if (!stopped) setFolderAvailable(answer.available);
      })
      .catch(() => {
        if (!stopped) setFolderAvailable(false);
      });
    return () => {
      stopped = true;
    };
  }, [api, allergensOpen]);

  /** "Folder bewerken" in ⚠ Allergenen: whoever may change the menu opens the folder to change and print it. */
  const editFolder = async () => {
    const authorization = await authorize("menu.edit", t.allergenCard.folderCode);
    if (!authorization) return;
    try {
      if (!(await openFolder(api, authorization.token, true))) toast(t.allergenCard.folderBlocked, "bad");
      setOpen(null);
    } catch (error) {
      report(error);
    }
  };

  /** A code at F3 (prototype handleCode): "2DP", "PEPM", "CC". The category of the product opens. */
  const enterCode = (raw: string) => {
    if (!menu) return;
    const found = findByCode(menu, raw);
    if (found === null) return;
    if (found.kind === "unknown") {
      toast(t.sale.codeUnknown(raw.trim()), "bad");
      return;
    }
    const { product, quantity } = found;
    if (soldOut.has(product.id)) {
      toast(t.soldOut.tile(product.name), "bad");
      return;
    }
    if (product.categoryId !== null) setCategory(product.categoryId);
    const size = found.variant ?? (oneTap(menu, product) ? defaultVariant(product) : undefined);
    if (hasOptions(menu, product) || (size === undefined && product.variants.length > 1)) {
      const preset = size ?? defaultVariant(product);
      setOpen({ kind: "product", product, quantity, ...(preset !== undefined && { variant: preset }) });
    } else {
      addLine(lineFor(product, { variant: size, quantity }));
    }
  };

  /** Changing a line (double click, F8); the free product of the action has no options to change. */
  const editLine = (line: CartLine) => {
    if (line.promotion === true || !menu) return;
    const product = menu.products[line.productId];
    if (product) setOpen({ kind: "product", product, editing: line });
  };

  const onLineDone = (line: LineRequest) => {
    if (open?.kind === "product" && open.promotion === true) {
      // the free product of the action with its choices: once, for free, no code needed
      dispatch({
        type: "add",
        key: newKey(),
        line: { ...(menu ? normalizedLine(menu, line) : line), quantity: 1, free: true, promotion: true },
      });
      toast(t.promotions.freeAdded);
    } else if (open?.kind === "product" && open.editing) {
      const changed = menu ? normalizedLine(menu, line) : line;
      dispatch({
        type: "replace",
        key: open.editing.key,
        line: { ...changed, ...(open.editing.free === true && { free: true }) },
      });
    } else {
      addLine(line);
    }
    setOpen(null);
  };

  /** Says what is missing and puts the cursor there (prototype canPay). */
  const explainMissing = (): boolean => {
    if (missing === null && freeToChoose !== null) {
      toast(t.promotions.chooseFirst(freeToChoose.name), "bad");
      return true;
    }
    if (missing === null) return false;
    if (missing !== "empty") toast(t.sale.missing[missing], "bad");
    if (missing === "zone" || missing === "street") customerFields.current?.focus(missing);
    return true;
  };

  /** "Herhaal": the customer's last order on the ticket again, as far as it can still be ordered. */
  const repeat = (lines: LineRequest[]) => {
    if (!menu) return;
    const { lines: kept, skipped } = repeatLines(lines, menu, soldOut);
    for (const line of kept) addLine(line);
    toast(skipped > 0 ? t.customer.repeatedPart(skipped) : t.customer.repeated);
  };

  /**
   * "Overnemen" (prototype bindCall): the number on the ticket, a table becomes a delivery; a known
   * customer is filled in by the ticket itself. The other tills stop showing the call. An order that
   * was being taken for somebody else goes to the Wachtlijst first, so nothing is lost (Furkan,
   * 07/10/2026: a second caller while the first order is on the ticket).
   */
  const takeCall = async (call: Call) => {
    callHandled();
    setOpen(null);
    api.request("POST", `/device/calls/${call.id}/take`).catch(report);
    if (editing) return;
    // products without a customer yet are this caller's: the number joins them, as before
    const mine = phoneDigits(cart.customer.phone);
    const other = mine !== "" && mine !== phoneDigits(call.phone);
    if (cart.lines.length > 0 && cart.channel !== "table" && other) {
      // not parked (no connection): the ticket stays as it is, the call is in the Bellerslijst (F10)
      if (!(await park())) return;
    }
    if (cart.channel === "table") dispatch({ type: "channel", channel: "delivery" });
    dispatch({ type: "fillCustomer", customer: { ...noCustomer(), phone: formatPhone(call.phone) } });
    if (call.customer === null) dispatch({ type: "note", value: "" });
  };

  /** "☎ Test oproep": a call as the caller-ID device would report it, on every till. */
  const testCall = () => {
    const number = TEST_CALLS[testCalls.current++ % TEST_CALLS.length]!;
    api.request("POST", "/device/calls", { body: { phone: number, source: "test" } }).catch(report);
  };

  /** Tafel without a table: the floor plan opens (as in the prototype). */
  const chooseChannel = (channel: Channel) => {
    dispatch({ type: "channel", channel });
    if (channel === "table" && (cart.channel !== "table" || cart.table === "")) setOpen({ kind: "tables" });
  };

  const pickTable = (table: string) => {
    dispatch({ type: "table", table, order: openTables.get(table) ?? null });
    setOpen(null);
  };

  /** The ticket of an open table saved into its order; returns the order as it is now. */
  /** A table to the kitchen with no kitchen printer set up: said in red (Papermoon, 08/10/2026). */
  const warnUnrouted = (order: Order) => {
    const missing = unroutedToast(order.prints);
    if (missing !== null) toast(missing, "bad");
  };

  const saveTable = async (token: string | undefined): Promise<Order | null> => {
    const current = cart.tableOrder ? openTables.get(cart.table) : undefined;
    if (!cart.tableOrder || !current) return null;
    if (!differsFrom(cart, current)) return current;
    const request = prepare(
      `/orders/${cart.tableOrder.id}`,
      toEditRequest(cart, cart.tableOrder.revision),
      t.tables.label(cart.table),
      { method: "PUT", staffToken: token },
    );
    const saved = await resend<Order>(api, request);
    orderSaved(saved);
    warnUnrouted(saved);
    return saved;
  };

  const checkout = async () => {
    if (explainMissing() || saving) return;
    const token = await discountToken();
    if (token === null) return;
    if (!cart.tableOrder) {
      setOpen({ kind: "checkout" });
      return;
    }
    setSaving(true);
    try {
      const order = await saveTable(token);
      // what was added first goes to the kitchen (BIJBESTELLING), then the bill
      if (toKitchen(order?.prints)) toast(t.tables.savedKitchen(cart.table));
      if (order) setOpen({ kind: "payTable", order });
    } catch (error) {
      report(error);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Save without payment: to the kitchen for tables (adding to an open table), paid at the door for
   * deliveries; the receipt of a delivery shows right away, with the route QR (prototype).
   */
  const saveOpen = async () => {
    if (explainMissing() || saving) return;
    const token = await discountToken();
    if (token === null) return;
    setSaving(true);
    try {
      if (cart.tableOrder) {
        const saved = await saveTable(token);
        // prototype: "Tafel 7 bewaard · bon naar keuken" when the kitchen got a ticket (BIJBESTELLING)
        toast(toKitchen(saved?.prints) ? t.tables.savedKitchen(cart.table) : t.tables.saved(cart.table));
      } else {
        const request = prepare("/orders", toCreateRequest(cart), t.sale.saveOpen[cart.channel], {
          staffToken: token,
        });
        const order = await resend<Order>(api, request);
        orderSaved(order);
        warnUnrouted(order);
        if (cart.channel === "table") {
          toast(toKitchen(order.prints) ? t.tables.savedKitchen(cart.table) : t.tables.saved(cart.table));
        } else {
          // a delivery (prototype): saved, "Keukenbon → …", "Kassabon → Kassaprinter toog (80 mm) ×2"
          toast(t.sale.saved(order.code));
          for (const text of printToasts(order.prints)) toast(text);
        }
        mailed(order, 0);
        if (order.channel === "delivery") setOpen({ kind: "receipt", order });
      }
      reset();
    } catch (error) {
      if (error instanceof ApiError && error.retryable) {
        // still pending: the till sends it by itself, so the ticket must not be saved a second time
        toast(t.checkout.deferred);
        reset();
      } else {
        report(error);
      }
    } finally {
      setSaving(false);
    }
  };

  /** Parkeren (Pause): the ticket goes to the Wachtlijst, on every device; nothing to the kitchen. */
  const park = async (): Promise<boolean> => {
    if (editing) {
      toast(t.editing.finishFirst, "bad");
      return false;
    }
    if (cart.lines.length === 0 || cart.channel === "table" || saving) return false;
    setSaving(true);
    try {
      await api.request("POST", "/device/parked", { body: toParkRequest(cart) });
      toast(t.parked.parked);
      reset();
      return true;
    } catch (error) {
      report(error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Saves an edited order (prototype saveEdit). On a paid order with another total the difference is
   * settled in the same step: "Bijbetalen" with a payment method, "Terug te geven" in cash or through
   * the terminal. Without a fresh price (offline) the server's answer decides what comes next.
   */
  const saveEdit = async (settle?: Settle) => {
    if (!editing || saving) return;
    if (cart.lines.length === 0) {
      toast(t.editing.noLines, "bad");
      return;
    }
    if (explainMissing()) return;
    if (!editChanges(cart, editing)) {
      toast(t.editing.nothing);
      onEditDone(null);
      return;
    }
    setSaving(true);
    if (settle === undefined && editing.paidCents > 0) {
      // a paid order: "Bijbetalen" or "Terug te geven" first, with the price of the ticket as it is now
      // (asked right away when the last change is not priced yet)
      let now: Quote | null;
      try {
        now =
          quote.fresh && quote.quote
            ? quote.quote
            : content
              ? await api.request<Quote>("POST", "/orders/quote", { body: content })
              : null;
      } catch (error) {
        setSaving(false);
        report(error);
        return;
      }
      if (now && now.totalCents !== editing.paidCents) {
        setSaving(false);
        setOpen({ kind: "settle", totalCents: now.totalCents, changes: lineChanges(editing.lines, now.lines) });
        return;
      }
    }
    const request = prepare(
      `/orders/${editing.id}`,
      {
        ...toEditRequest(cart, editing.revision),
        // "Korting" chosen while editing: priced again by the server; otherwise the order keeps what it has
        ...(discountChanged(cart, editing) && { discount: cart.discount }),
        ...settle,
      },
      t.editing.bar(editing.code),
      { method: "PUT", staffToken },
    );
    try {
      const updated = await resend<Order>(api, request);
      orderSaved(updated);
      warnUnrouted(updated);
      setOpen(null);
      // prototype saveEdit: the drawer, BIJBESTELLING / GESCHRAPT with what changed, "Nieuwe kassabon …"
      for (const text of editToasts(
        updated.prints,
        lineChanges(editing.lines, updated.lines),
        updated.code,
        formatEuro(updated.totalCents),
      )) {
        toast(text);
      }
      mailed(updated, editing.mails);
      const due = updated.totalCents - updated.paidCents;
      if (updated.channel === "delivery" && updated.status === "open" && updated.totalCents !== editing.totalCents) {
        toast(t.editing.courierCollects(formatEuro(due)));
      }
      const handBack = editing.paidCents - updated.paidCents;
      if (settle === undefined && handBack > 0) setOpen({ kind: "giveBack", order: updated, amountCents: handBack });
      else if (updated.status === "open" && updated.paidCents > 0) setOpen({ kind: "payDifference", order: updated });
      else onEditDone(updated);
    } catch (error) {
      if (error instanceof ApiError && error.retryable) {
        toast(t.checkout.deferred);
        onEditDone(null);
      } else {
        report(error);
      }
    } finally {
      setSaving(false);
    }
  };

  const onPaid = (order: Order) => {
    toast(`${t.sale.saved(order.code)} · ${formatEuro(order.totalCents)}`);
    // prototype: "Keukenbon → …", "Kassabon → …", "Kassalade open (contant)"
    for (const text of printToasts(order.prints)) toast(text);
    reset();
    setOpen(null);
  };

  /** "✉ Mail naar klant: …" when the server mailed the customer about this order (prototype). */
  const mailed = (order: Order, before: number) => {
    if (order.mails > before) toast(t.orders.mails.sentTo(order.customer.email ?? ""));
  };

  /** F12 / F11: save the edit, order the delivery or pay (prototype payOrSave). */
  const payOrSave = () => {
    if (editing) void saveEdit();
    else if (delivery) void saveOpen();
    else void checkout();
  };

  /** Pause: Parkeren, at a table "Naar keuken"; nothing while editing (prototype). */
  const pause = () => {
    if (editing) return;
    if (cart.channel === "table") void saveOpen();
    else void park();
  };

  useImperativeHandle(ref, () => ({
    busy: () => editing !== null || cart.lines.length > 0,
    editing: () => editing !== null,
    park,
  }));

  /** The keys of the prototype: F1 F3 F8 F9 F10 F11 F12 Pause, Delete, Shift+G, + and −. */
  const onKey = useEffectEvent((event: KeyboardEvent) => {
    if (!active || open !== null || dialogOpen()) return;
    const handled = () => {
      event.preventDefault();
    };
    switch (event.key) {
      case "F1":
        handled();
        newOrder();
        return;
      case "F3":
        handled();
        codeBox.current?.focus();
        return;
      case "F8":
        handled();
        if (selected) editLine(selected);
        return;
      case "F9":
        handled();
        if (selected && selected.promotion !== true)
          dispatch({ type: "duplicate", key: selected.key, newKey: newKey() });
        return;
      case "F10":
        handled();
        setOpen({ kind: "calls" });
        return;
      case "F11":
      case "F12":
        handled();
        payOrSave();
        return;
      case "Pause":
        handled();
        pause();
        return;
    }
    // Delete, Shift+G, + and − belong to the text being typed in a field
    const target = event.target;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement
    ) {
      return;
    }
    if (selected === null) return;
    if (event.key === "Delete") {
      handled();
      dispatch({ type: "remove", key: selected.key });
    } else if (event.key === "G" && event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
      handled();
      void toggleFree(selected);
    } else if (event.key === "+") {
      dispatch({ type: "changeQuantity", key: selected.key, delta: 1 });
    } else if (event.key === "-") {
      dispatch({ type: "changeQuantity", key: selected.key, delta: -1 });
    }
  });
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      onKey(event);
    };
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, []);

  if (!menu || !rules) {
    return (
      <div className="panel" hidden={!active}>
        <p className="empty">{t.errors.menuMissing}</p>
      </div>
    );
  }

  const request = cart.lines.length > 0 ? toCreateRequest(cart) : null;
  const empty = cart.lines.length === 0;

  /** The loyalty card at checkout: a known customer, and the action Spaarkaart on for this order. */
  const loyaltyOf = (): Loyalty | undefined => {
    const action = promotions?.points;
    if (!customer || !action?.on || !action.channels[cart.channel]) return undefined;
    return { points: customer.points, goal: action.goal, rewardCents: action.rewardCents, used: cart.redeemPoints };
  };

  return (
    <div className="sale" hidden={!active} data-pane={pane}>
      <div className="sale-panes" role="tablist" aria-label={t.sale.panes}>
        <button
          type="button"
          role="tab"
          aria-selected={pane === "menu"}
          onClick={() => {
            setPane("menu");
          }}
        >
          {t.sale.paneMenu}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={pane === "order"}
          onClick={() => {
            setPane("order");
          }}
        >
          {t.sale.paneOrder(cart.lines.reduce((count, line) => count + line.quantity, 0))}
        </button>
      </div>
      <Ticket
        editing={editing ? { code: editing.code, totalCents: editing.totalCents } : null}
        onStopEdit={stopEdit}
        cart={cart}
        dispatch={dispatch}
        menu={menu}
        quote={quote}
        onToggleFree={(line) => void toggleFree(line)}
        onChannel={chooseChannel}
        onChooseTable={() => {
          setOpen({ kind: "tables" });
        }}
        onMoveTable={() => {
          setOpen({ kind: "moveTable", from: cart.table });
        }}
        newKey={newKey}
        onAddFree={(offer) => {
          // a product with something to choose (Papi's: a snack of choice) opens its dialog first
          const product = menu.products[offer.productId];
          if (product && hasOptions(menu, product)) {
            setOpen({
              kind: "product",
              product,
              quantity: 1,
              promotion: true,
              ...(offer.variant !== null && { variant: offer.variant }),
            });
            return;
          }
          dispatch({
            type: "add",
            key: newKey(),
            line: {
              productId: offer.productId,
              ...(offer.variant !== null && { variant: offer.variant }),
              quantity: 1,
              free: true,
              promotion: true,
            },
          });
          toast(t.promotions.freeAdded);
        }}
        onEdit={editLine}
        onRepeat={repeat}
        onCalls={() => {
          setOpen({ kind: "calls" });
        }}
        onTakeCall={(call) => void takeCall(call)}
        customerRef={customerFields}
      >
        {/* prototype: Parkeren (at a table "Naar keuken"), Nieuw, Afrekenen; decisions 30/09/2026:
            takeaway is always paid at once, delivery is paid at the door ("Bestellen") */}
        <div className="pay">
          {discountWhileEditing ? (
            // nothing is parked or sent to the kitchen while editing: its place is for "Korting"
            <button
              type="button"
              className="bigbtn"
              disabled={saving}
              onClick={() => {
                setOpen({ kind: "editDiscount" });
              }}
            >
              {t.editing.discount(cart.discount ? discountLabel(cart.discount) : null)}
            </button>
          ) : cart.channel === "table" ? (
            <button
              type="button"
              className="bigbtn kitchen"
              disabled={editing !== null || empty || saving}
              onClick={() => void saveOpen()}
            >
              {t.sale.saveOpen.table}
              <kbd className="hint">{t.sale.saveOpenHint.table}</kbd>
            </button>
          ) : (
            <button
              type="button"
              className="bigbtn"
              disabled={editing !== null || empty || saving}
              onClick={() => void park()}
            >
              {t.sale.park}
              <kbd>Pause</kbd>
            </button>
          )}
          <button type="button" className="bigbtn" onClick={newOrder}>
            {t.sale.newOrder}
            <kbd>F1</kbd>
          </button>
          {editing ? (
            <button type="button" className="bigbtn primary" disabled={saving} onClick={() => void saveEdit()}>
              {t.editing.save}
              <kbd>F12</kbd>
            </button>
          ) : delivery ? (
            <button type="button" className="bigbtn primary" disabled={empty || saving} onClick={() => void saveOpen()}>
              {t.sale.saveOpen.delivery}
              <kbd className="hint">
                {t.sale.saveOpenHint.delivery}
                <span className="key"> · F12</span>
              </kbd>
            </button>
          ) : (
            <button type="button" className="bigbtn primary" disabled={empty || saving} onClick={() => void checkout()}>
              {t.sale.checkout}
              <kbd>F12</kbd>
            </button>
          )}
        </div>
      </Ticket>

      <MenuPanel
        menu={menu}
        channel={cart.channel}
        category={category}
        onCategory={setCategory}
        onProduct={tapProduct}
        head={
          <>
            <CodeBox ref={codeBox} onCode={enterCode} />
            {testCallOffered() && (
              <button type="button" className="btn ghost" title={t.calls.testTitle} onClick={testCall}>
                {t.calls.test}
              </button>
            )}
            <button
              type="button"
              className="btn ghost calls-menu"
              onClick={() => {
                setOpen({ kind: "calls" });
              }}
            >
              {t.calls.list} <span className="code">F10</span>
            </button>
            <button
              type="button"
              className="btn ghost"
              title={t.sale.allergensTitle}
              onClick={() => {
                setOpen({ kind: "allergens" });
              }}
            >
              {t.sale.allergens}
            </button>
            <span className="proto hintline">{t.sale.codeHint}</span>
          </>
        }
      />

      {open?.kind === "product" && (
        <ProductDialog
          api={api}
          menu={menu}
          rules={rules}
          product={open.product}
          channel={cart.channel}
          {...(open.editing && { editing: open.editing })}
          {...(open.variant !== undefined && { variant: open.variant })}
          {...(open.quantity !== undefined && { quantity: open.quantity })}
          onDone={onLineDone}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {open?.kind === "tables" && (
        <TablePicker
          current={cart.table}
          onPick={pickTable}
          onMove={(from) => {
            setOpen({ kind: "moveTable", from });
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {open?.kind === "moveTable" && (
        <MoveTableDialog
          from={open.from}
          onMoved={(order) => {
            dispatch({ type: "tableMoved", order });
            setOpen(null);
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {open?.kind === "payTable" && (
        <CheckoutDialog
          target={{ kind: "existing", order: open.order }}
          onDiscount={(discount) => void discountTable(open.order, discount)}
          onPaid={onPaid}
          onClose={() => {
            // what was added is saved on the table; the ticket shows the table as it is now
            pickTable(open.order.table ?? cart.table);
          }}
        />
      )}
      {open?.kind === "editDiscount" && editing && (
        <Dialog
          title={t.editing.discountTitle}
          size="narrow"
          onClose={() => {
            setOpen(null);
          }}
        >
          <DiscountButtons
            discounts={discounts}
            chosen={cart.discount}
            onChoose={(discount) => void chooseEditDiscount(discount)}
          />
          {editing.paidCents > 0 && <p className="note">{t.editing.discountPaid}</p>}
        </Dialog>
      )}
      {open?.kind === "settle" && editing && (
        <EditSettleDialog
          order={editing}
          totalCents={open.totalCents}
          changes={open.changes}
          {...(discountChanged(cart, editing) && { discount: cart.discount })}
          methods={reopened?.authorization?.staff.acceptedPaymentMethods ?? paymentMethods}
          busy={saving}
          onSettle={(settle) => void saveEdit(settle)}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {open?.kind === "payDifference" && (
        <CheckoutDialog
          target={{ kind: "existing", order: open.order }}
          onPaid={(paid) => {
            for (const text of printToasts(paid.prints)) toast(text);
            onEditDone(paid);
          }}
          onClose={() => {
            onEditDone(open.order);
          }}
        />
      )}
      {open?.kind === "giveBack" && (
        <Dialog title={t.checkout.change} size="narrow" onClose={() => onEditDone(open.order)}>
          <div className="done">
            <p className="big change num">{t.editing.giveBack(formatEuro(open.amountCents))}</p>
            <button type="button" className="btn primary" data-autofocus onClick={() => onEditDone(open.order)}>
              OK
            </button>
          </div>
        </Dialog>
      )}
      {open?.kind === "receipt" && (
        <ReceiptDialog
          order={open.order}
          printed={open.order.prints?.find((notice) => notice.kind === "receipt")}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {open?.kind === "calls" && (
        <CallsDialog
          onTake={(call) => void takeCall(call)}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {open?.kind === "allergens" && (
        <AllergenCard
          menu={menu}
          onClose={() => {
            setOpen(null);
          }}
          {...(folderAvailable && { onFolder: () => void editFolder() })}
        />
      )}
      {open?.kind === "checkout" && request && (
        <CheckoutDialog
          target={{
            kind: "new",
            request,
            totalCents: quote.fresh && quote.quote ? quote.quote.totalCents : null,
            discount: cart.discount,
            staffToken,
          }}
          onDiscount={(discount) => void chooseDiscount(discount)}
          loyalty={loyaltyOf()}
          onRedeem={(used) => {
            dispatch({ type: "redeem", on: used });
          }}
          onPaid={(order) => {
            onPaid(order);
            mailed(order, 0);
          }}
          onDeferred={() => {
            toast(t.checkout.deferred);
            reset();
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </div>
  );
}
